import { Room } from '@spike/room-model';
import { searchThunk, RoomsStatus } from '@spike/rooms-action';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EditRoom from './edit/EditRoom';
import styles from './Rooms.module.scss';
import { RootState } from 'store';
import { PageResult } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import RoomsTable from './RoomsTable';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import Spinner from 'components/UI/V3/Spinner';
import { useMediaQuery } from 'react-responsive';
import DefaultTableEmptyState from 'components/UI/V3/Table/EmptyState/DefaultTableEmptyState';

export interface RoomsProps {
    isMobile?: boolean;
    className?: string;
    openSidebar?: () => void;
}

export const Rooms: FunctionComponent<RoomsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const roomsPage = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);

    const [loading, setLoading] = useState({ rooms: true, initialLoading: true });
    const [pageInfo, setPageInfo] = useState({ searchText: '', page: 1, pageSize: 10 });

    const search = () => {
        setLoading(prev => ({ ...prev, rooms: true }));
        dispatch(searchThunk(apiClientWrapper, pageInfo.searchText, pageInfo.page, pageInfo.pageSize));
    };

    useEffect(() => {
        dispatch(fetchStaffThunk());
    }, []);

    useEffect(() => {
        search();
    }, [pageInfo]);

    useNonInitialEffect(() => {
        switch (roomsStatus) {
            case RoomsStatus.SearchSuccess:
            case RoomsStatus.Error:
                setLoading(prev => ({ ...prev, rooms: false, initialLoading: false }));
        }
    }, [roomsStatus]);

    const [editRoom, setEditRoom] = useState<{ show: boolean; room: undefined | Room }>({
        show: false,
        room: undefined
    });

    const savedHandler = () => {
        setEditRoom(prev => ({ ...prev, show: false, room: undefined }));
        search();
    };

    return (
        <>
            <div className={styles.container}>
                <DefaultHeader
                    title="Rooms & Spaces"
                    buttonLabel={isMobile ? 'Add' : 'Add Room or Space'}
                    textBelowTitle={
                        isMobile
                            ? 'Manage your Daycare & Boarding rooms & spaces.'
                            : 'Manage your Daycare, Boarding, Grooming, and Training spaces.'
                    }
                    displayButton={true}
                    onClick={() => setEditRoom(prev => ({ ...prev, show: true, room: undefined }))}
                />
                {loading.initialLoading ? (
                    <div className={styles.spinnerContainer}>
                        <Spinner />
                    </div>
                ) : (roomsPage.pagination?.totalRecords ?? 0) > 0 || pageInfo.searchText.length > 0 ? (
                    <>
                        <SearchBar
                            placeholder="Search by room name"
                            searchText=""
                            onChangeSearchText={searchText => setPageInfo(prev => ({ ...prev, searchText }))}
                            className={styles.searchBar}
                        />
                        <RoomsTable
                            roomsPage={roomsPage}
                            isLoading={loading.rooms}
                            className={styles.table}
                            onPageChange={page => setPageInfo(prev => ({ ...prev, page }))}
                            onPageSizeChange={pageSize => setPageInfo(prev => ({ ...prev, pageSize }))}
                        />
                    </>
                ) : null}
            </div>
            {editRoom.show && (
                <EditRoom
                    room={editRoom.room}
                    onClose={() => setEditRoom(prev => ({ ...prev, show: false, room: undefined }))}
                    onSaved={savedHandler}
                />
            )}
        </>
    );
};

export default Rooms;
